
import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { db } from "../../firebaseClient";
import { DataToolbar } from "../components/DataToolbar";
import { Modal } from "../components/Modal";
import { useToast } from "../components/Toast";
import { isGovAdmin, canViewAllData, isDeptMinimal, getDeptCodeByEmail } from "../config/roles";
import { ApprovalFlowPanel } from "../components/approval/ApprovalFlowPanel";
import { createOrResetApprovalFlow, updateStageDecision } from "../services/approvalFlowService";
import { useApprovalFlow } from "../hooks/useApprovalFlow";
import { FileUploadKit } from "../components/common/FileUploadKit";
import { uploadGovernanceDocFiles } from "../services/storageUpload";
import { DEPARTMENTS, getDeptNameByCode } from "../constants/departments";
import type { AttachmentInfo } from "../types/legalOps";
import { generateDocCode } from "../utils/idGenerator";
import { canViewData } from "../utils/dataAccess";

const GOV_DOC_CATEGORIES = [
  "公司治理與董事會運作",
  "人資規範與勞動條件",
  "資訊安全與個資保護",
  "財務與會計／稅務管理",
  "採購與供應商管理",
  "品牌／行銷／公關政策",
  "營運流程與內部 SOP",
  "品質管理與客訴處理",
  "環境／職安衛（EHS）",
  "風險管理與內控制度",
  "其他"
];

export function GovernanceCoreDocsPage() {
  const { user, debugViewDepartmentId } = useAuth();
  const [activeTab, setActiveTab] = useState<'PUBLISHED' | 'DRAFT'>('PUBLISHED');
  const [items, setItems] = useState<any[]>([]);
  const [viewItem, setViewItem] = useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  const isSuperUser = isGovAdmin(user?.email);
  const isDept = isDeptMinimal(user?.email);

  useEffect(() => {
      const unsub = db.collection("governance_core_docs").orderBy("updatedAt", "desc").onSnapshot(snap => {
          const all = snap.docs.map(d => ({id: d.id, ...d.data()}));
          let filtered = all;

          if (activeTab === 'PUBLISHED') {
              // Filter for Published docs (EXCEPTION: Public/Published docs are visible to everyone)
              // We rely on the status field.
              filtered = all.filter((d: any) => 
                  d.status === 'published' || 
                  d.status === '生效中' || 
                  d.visibility === 'public'
              );
          } else {
              // Drafts (Internal Workspace)
              // Only super users or authorized dept users can see drafts
              filtered = all.filter((d: any) => 
                  d.status !== 'published' && 
                  d.status !== '生效中'
              );
              
              // Apply standard visibility check for drafts
              // Construct a user object if needed, but 'canViewData' expects AppUser
              // We pass the current user from context
              filtered = filtered.filter(doc => canViewData(user, doc));
          }

          setItems(filtered);
      });
      return () => unsub();
  }, [activeTab, isSuperUser, user]);

  return (
    <div style={{height:'100%', display:'flex', flexDirection:'column'}}>
        <div className="row" style={{borderBottom: '1px solid #333', marginBottom: 16}}>
            <button className="navbtn" style={{width:'auto', borderBottom: activeTab==='PUBLISHED'?'2px solid #fff':'none'}} onClick={() => setActiveTab('PUBLISHED')}>正式公告</button>
            {/* Hide Workspace for Dept Minimal users */}
            {(!isDept) && (
                <button className="navbtn" style={{width:'auto', borderBottom: activeTab==='DRAFT'?'2px solid #fff':'none'}} onClick={() => setActiveTab('DRAFT')}>工作區</button>
            )}
        </div>

        <DataToolbar 
            canEdit={activeTab === 'DRAFT' || isSuperUser} 
            onCreate={() => { setViewItem(null); setIsEditModalOpen(true); }} 
            onExport={()=>{}} 
            title={activeTab === 'PUBLISHED' ? "正式公告文件 (全公司可見)" : "文件草案列表 (僅授權部門可見)"} 
        />
        
        <div style={{display:'grid', gap:10, overflowY:'auto', flex:1}}>
            {items.map(item => (
                <div key={item.id} className="tile" style={{display:'flex', justifyContent:'space-between', alignItems:'center', borderLeft: item.status==='published' ? '4px solid var(--ok)' : '4px solid #444'}}>
                    <div>
                        <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:4}}>
                            {item.category && <span className="badge" style={{fontSize:11, padding:'2px 6px'}}>{item.category}</span>}
                            <span style={{fontFamily:'monospace', color:'#fbbf24', fontSize:13}}>{item.docNo}</span>
                        </div>
                        <div style={{fontWeight:'bold', color:'#fff', fontSize:15}}>{item.title}</div>
                        <div style={{fontSize:13, color:'var(--muted)', marginTop:4}}>
                            版本: {item.version || 'v1.0'} | 部門: {item.ownerDept} | 更新: {item.updatedAt?.slice(0,10)}
                        </div>
                    </div>
                    <div style={{textAlign:'right'}}>
                        <span className="badge" style={{marginBottom:4, display:'inline-block'}}>{item.status}</span>
                        <div style={{marginTop:4}}>
                            <button className="smallbtn" onClick={() => { setViewItem(item); setIsEditModalOpen(true); }}>
                                {activeTab === 'PUBLISHED' && !isSuperUser ? "檢視" : "管理 / 簽核"}
                            </button>
                        </div>
                    </div>
                </div>
            ))}
            {items.length === 0 && <div className="p" style={{padding:20, textAlign:'center'}}>無資料</div>}
        </div>

        {isEditModalOpen && (
            <CoreDocModal 
                item={viewItem} 
                existingItems={items}
                onClose={() => setIsEditModalOpen(false)} 
                isOfficial={activeTab === 'PUBLISHED'}
                canEdit={activeTab === 'DRAFT' || isSuperUser}
            />
        )}
    </div>
  );
}

function CoreDocModal({ item, existingItems, onClose, isOfficial, canEdit }: any) {
    const { user } = useAuth();
    const { showToast } = useToast();
    
    // Form State
    const [form, setForm] = useState(item || { 
        title: "", docNo: "", ownerDept: "法務部", 
        version: "v1.0", description: "", status: "draft",
        category: "", // New Category Field
        attachments: [], reviewAttachments: [], kcAttachments: [],
        visibility: "restricted" // Default restricted unless published
    });
    
    // Auto Generate DocNo (POL-YYYYMM-XXX) if new
    useEffect(() => {
        if (!item && !form.docNo) {
            // Use 'policy' prefix and monthlyOnly=true
            const newCode = generateDocCode("policy", new Date(), existingItems, true);
            setForm((p: any) => ({ ...p, docNo: newCode }));
        }
    }, [item]);

    // Flow
    const { flow, loading } = useApprovalFlow("CORE_DOC", item?.id || "new");

    const handleSave = async () => {
        if (!form.title) return alert("請填寫標題");
        if (!form.category) return alert("請選擇公告分類");
        
        const payload = { ...form, updatedAt: new Date().toISOString() };
        
        // Auto set public if status is published/effecting
        if (payload.status === 'published' || payload.status === '生效中') {
            payload.visibility = 'public';
        }

        let docId = item?.id;
        if (docId) {
            await db.collection("governance_core_docs").doc(docId).update(payload);
        } else {
            payload.createdAt = new Date().toISOString();
            // Ensure docNo is set if missing (rare case)
            if (!payload.docNo) {
                payload.docNo = generateDocCode("policy", new Date(), existingItems, true);
            }
            const ref = await db.collection("governance_core_docs").add(payload);
            docId = ref.id;
        }
        showToast("儲存成功");
        onClose();
    };

    const startFlow = async () => {
        if (!item?.id) return alert("請先儲存草稿");
        await createOrResetApprovalFlow("CORE_DOC", item.id, user?.id || "", form.ownerDept, user?.displayName);
        await db.collection("governance_core_docs").doc(item.id).update({ status: 'in_review' });
        showToast("已啟動簽核");
    };

    const handleDecision = async (
      stageKey: string,
      decision: "APPROVED" | "REJECTED" | "DONE" | "RETURNED",
      comment: string,
      deptCode?: string,
    ) => {
        void deptCode;
        if (decision === "DONE") return;

        if (!flow) return;
        const safeDecision = decision as "APPROVED" | "REJECTED" | "RETURNED" | "DONE";
        
        await updateStageDecision({ 
            flowId: flow.id, 
            stageKey: stageKey as any, 
            decision: safeDecision, 
            comment,
            actorUid: user?.id || 'unknown',
            actorName: user?.displayName || 'User',
            deptCode
        });
        
        if (decision === 'REJECTED') {
            await db.collection("governance_core_docs").doc(item.id).update({ status: 'draft', visibility: 'restricted' }); // Revert to draft
        } else if (decision === 'APPROVED' && stageKey === 'MANAGER_IRENE') {
            // Upon final approval, publish and make public
            await db.collection("governance_core_docs").doc(item.id).update({ status: 'published', visibility: 'public' });
        }
        
        showToast("已審核");
    };

    return (
        <Modal title={item ? (isOfficial ? "公告詳情" : "草稿編輯") : "新增文件"} onClose={onClose} width={900}>
            <div style={{display:'grid', gridTemplateColumns:'1fr 350px', gap:24}}>
                <div>
                    <div className="grid">
                        <div className="field"><label className="label">公告編號 (系統自動)</label>
                            <input className="input" value={form.docNo} readOnly style={{background:'rgba(255,255,255,0.05)', color:'#fbbf24', fontFamily:'monospace'}} />
                        </div>
                        <div className="field"><label className="label">標題</label><input className="input" value={form.title} onChange={e=>setForm({...form, title:e.target.value})} disabled={!canEdit} /></div>
                        
                        <div className="field"><label className="label">部門</label>
                            <select className="input" value={form.ownerDept} onChange={e=>setForm({...form, ownerDept:e.target.value})} disabled={!canEdit}>
                                {DEPARTMENTS.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                            </select>
                        </div>
                        
                        <div className="field"><label className="label">公告分類 <span style={{color:'red'}}>*</span></label>
                            <select className="input" value={form.category} onChange={e=>setForm({...form, category:e.target.value})} disabled={!canEdit}>
                                <option value="" disabled>請選擇分類</option>
                                {GOV_DOC_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>

                        <div className="field"><label className="label">版本</label><input className="input" value={form.version} onChange={e=>setForm({...form, version:e.target.value})} disabled={!canEdit} /></div>
                        
                        {canEdit && (
                            <div className="field">
                                <label className="label">可見性 (Public/Restricted)</label>
                                <select className="input" value={form.visibility || 'restricted'} onChange={e=>setForm({...form, visibility:e.target.value})}>
                                    <option value="restricted">受限 (僅部門與高層)</option>
                                    <option value="public">公開 (全公司)</option>
                                </select>
                            </div>
                        )}

                        <div className="field" style={{gridColumn:'1/-1'}}><label className="label">說明</label><textarea className="input" rows={4} value={form.description} onChange={e=>setForm({...form, description:e.target.value})} disabled={!canEdit} /></div>
                    </div>
                    
                    <div style={{marginTop:20}}>
                        <FileUploadKit 
                            existingFiles={form.attachments} 
                            disabled={!canEdit}
                            onFilesSelected={async (fs) => {
                                const newFiles = await uploadGovernanceDocFiles(item?.id || 'temp', fs);
                                setForm((p: any) => ({...p, attachments: [...(p.attachments||[]), ...newFiles]}));
                            }}
                        />
                    </div>

                    {canEdit && (
                        <div style={{marginTop:20, display:'flex', gap:10, justifyContent:'flex-end'}}>
                            <button className="btn btn-outline" style={{width:'auto'}} onClick={handleSave}>儲存內容</button>
                            {!isOfficial && !flow && (
                                <button className="btn" style={{width:'auto'}} onClick={startFlow}>啟動簽核流程</button>
                            )}
                        </div>
                    )}
                </div>
                
                <div>
                    <div className="label">簽核流程</div>
                    {loading ? <div>Loading...</div> : (
                        flow ? <ApprovalFlowPanel 
                            flow={flow} 
                            onDecision={handleDecision} 
                            requestId={item?.id} 
                            reviewAttachments={form.reviewAttachments}
                            kcAttachments={form.kcAttachments}
                            onReviewAttachmentsChange={(files: AttachmentInfo[]) => setForm((p: any) => ({...p, reviewAttachments: files}))}
                            onKcAttachmentsChange={(files: AttachmentInfo[]) => setForm((p: any) => ({...p, kcAttachments: files}))}
                        /> : <div className="p">尚未啟動流程</div>
                    )}
                </div>
            </div>
        </Modal>
    );
}
